import { Fragment, useContext, useMemo } from 'react';

import Box from '@mui/joy/Box';
import ListDivider from '@mui/joy/ListDivider';
import ListItem from '@mui/joy/ListItem';
import ListItemButton, { ListItemButtonProps } from '@mui/joy/ListItemButton';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import CircleIcon from '@mui/icons-material/Circle';
import AvatarWithStatus from './AvatarWithStatus';
import WarningIcon from '@mui/icons-material/Warning';

import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale'; // Для русского языка
import { useNavigate } from 'react-router';
import { Badge, Chip, Tooltip } from '@mui/joy';
import { LayoutContext } from '../../context';
import { canSeeDangerMarks } from '../../utils/dangerVisibility';


const TimeAgo = ({ datetime }) => {
  if (!datetime) return null;

  const date = new Date(datetime);
  const diff = Date.now() - date.getTime();
  // «в будущем» или меньше минуты → «только что» (баг #5)
  if (diff < 60 * 1000) return 'только что';

  // locale: ru уже возвращает русский текст, ручные replace не нужны
  return formatDistanceToNow(date, { addSuffix: true, locale: ru });
};


export default function ChatListItem({ chat, onSelect, selected }) {
  const navigate = useNavigate();
  const { unreadCounts, account } = useContext(LayoutContext);

  const countUnread = useMemo(() => {
    if (!chat?.chat_id || !unreadCounts?.unread_counts) return null;

    // console.log("unreadCounts", unreadCounts, chat.chat_id);
    const chatUnread = unreadCounts?.unread_counts.find(item => item.chat_id == chat.chat_id);
    // console.log("unread11111Counts", chatUnread);
    return chatUnread ? chatUnread.unread_count : null;
  }, [unreadCounts, chat?.chat_id]);

  // Получаем количество участников чата
  const participantsCount = chat?.participants?.length || 0;
  const shouldShowParticipantsCount = participantsCount > 2;

  // Проверяем, является ли чат опасным
  const showDanger = canSeeDangerMarks(account);
  const isDangerousChat = showDanger && chat?.is_dangerous === true;
  // Служебный счётчик пациенту не используется даже из старого состояния.
  const dangerMessagesCount = showDanger ? Number(chat?.danger_messages_count || 0) : 0;


  return (
    <Fragment>
      <ListItem>
        <ListItemButton
          onClick={() => {
            onSelect(chat);
            navigate(`/chats?chatId=${chat.chat_id}`, { replace: true });
          }}
          selected={selected}
          color="neutral"
          sx={{ flexDirection: 'column', alignItems: 'initial', gap: 0 }}
        >
          <Stack direction="row" spacing={1}>
            <Box sx={{ position: 'relative' }}>
              <AvatarWithStatus alt={chat.display_name} />
              {/* Иконка опасного чата */}
              {isDangerousChat && (
                <Tooltip
                  title={`Опасный чат (${dangerMessagesCount} опасных сообщений)`}
                  placement="top"
                  arrow
                >
                  <WarningIcon 
                    sx={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      fontSize: 20,
                      color: 'danger.500',
                      backgroundColor: 'danger.200',
                      borderRadius: '50%',
                      padding: 0.5,
                      boxShadow: '0 0 4px rgba(0,0,0,0.3)'
                    }}
                  />
                </Tooltip>
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography level="title-md" fontWeight={700}>
                  {chat.display_name}
                </Typography>
              </Stack>

              {/* <Typography level="body-sm">{sender.username}</Typography> */}
              <Typography
                level="body-xs"
                fontWeight={countUnread ? 800 : 400}
                sx={(theme) => ({
                  display: '-webkit-box',
                  WebkitLineClamp: '1',
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  // color: countUnread ? 'primary.500' : 'neutral.500'
                })}
              >
                {chat?.last_message?.content}
              </Typography>
            </Box>
            <Box sx={{ lineHeight: 1, textAlign: 'right' }}>
              {countUnread && (
                <Badge
                  size="md"
                  color="primary"
                  variant="solid"
                  badgeContent={countUnread}
                  sx={{ minWidth: 'auto', px: 0.5 }}
                >
                  
                </Badge>
              )}
              {/* {countUnread && (
                <CircleIcon sx={{ fontSize: 12, color: 'primary.500', mb: 0.5 }} />
              )} */}
              {/* <Typography
                level="body-xs"
                noWrap
                sx={{ display: { xs: 'none', md: 'block' } }}
              >
                <TimeAgo datetime={chat?.last_msg?.datetime} />
              </Typography> */}
            </Box>
          </Stack>
          
          <Stack direction={'row'} spacing={0.5} sx={{ mt: 1 }} alignItems={'center'} justifyContent={'space-between'}>
            <Stack direction={'row'} spacing={1} alignItems={'center'} justifyContent={'flex-start'}>
              {/* Отображение участников чата (до 3-х имен) */}
              {chat.participants && chat.participants.length > 1 && (
                <Chip 
                  size="sm" 
                  variant="soft" 
                  color="primary"
                  sx={{ fontSize: '0.7rem', height: '20px' }}
                >
                  {/* {chat.participants.slice(0, 3).map(p => p.name).join(', ')} */}
                  {/* {participantsCount > 3 && ` и еще ${participantsCount - 3}`} */}
                  <>{chat.participants?.length} участ.</>
                </Chip>
              )}

              {!chat.is_stranger && (
                <Tooltip title="Чат где вы есть" arrow>
                  <Chip 
                    size="sm" 
                    variant="solid" 
                    color="success"
                    sx={{ fontSize: '0.7rem', height: '20px' }}
                  >
                    Вы в чате
                  </Chip>
                </Tooltip>
              )}

              {/* Чип для опасного чата */}
              {isDangerousChat && (
                <Tooltip title="Чат содержит опасные сообщения" arrow>
                  <Chip 
                    size="sm" 
                    variant="solid" 
                    color="warning"
                    startDecorator={<WarningIcon sx={{ fontSize: 14 }} />}
                    sx={{ fontSize: '0.7rem', height: '20px' }}
                  >
                    Опасный
                  </Chip>
                </Tooltip>
              )}

              {/* Добавляем чип для "чужого" чата */}
              {chat.is_stranger && (
                <Chip 
                  size="sm" 
                  variant="soft" 
                  color="warning"
                  sx={{ fontSize: '0.7rem', height: '20px' }}
                >
                  Частный чат
                </Chip>
              )}
            </Stack>

            <Typography
              level="body-xs"
              noWrap
              sx={{ display: 'block', textAlign: 'end' }}
            >
              <TimeAgo datetime={chat?.last_message?.date_create} />
            </Typography>
          </Stack>

        </ListItemButton>
      </ListItem>
      <ListDivider sx={{ margin: 0 }} />
    </Fragment>
  );
}
